import type { SensorType } from "../../../../shared.js";
import { DirectSubChannel, SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig, SensorElementOutput } from "../types.js";

// ─── Badger Meter 228PV series (PWM flowmeters) ──────────────────────────────
//
// Legacy calibration values use a formula: result [L/min] = kFactor_old * (freq + offset)
// where kFactor_old has effective units of (L/min)/Hz.
//
// The flowmeter sensor element uses:
//   kFactor [L/pulse]      = kFactor_old / 60
//   frequencyOffset [Hz]   = offset  (same value, preserved directly)
//   slope = kFactor * 60  = kFactor_old  (recovered in the element)
//   offset = frequencyOffset * slope = kFactor_old * offset
//
// This preserves the original calibration exactly.

type BadgermeterParams = {
    /** Legacy k-factor in (L/min)/Hz — divided by 60 to get L/pulse for the element */
    kFactor_old: number;
    /** Frequency offset (Hz) from the calibration curve: result = kFactor_old * (freq + offset) */
    frequencyOffset: number;
    minRawValueHz: number;
    modelName: string;
    friendlyName: string;
    friendlyShortName: string;
    legacySensorType: SensorType;
};

function badgermeterFlowmeterCluster({
    kFactor_old,
    frequencyOffset,
    minRawValueHz,
    modelName,
    friendlyName,
    friendlyShortName,
    legacySensorType,
}: BadgermeterParams): SensorClusterConfig<Record<string, never>> {
    const kFactor = kFactor_old / 60;

    const sensorElements: SensorElementOutput<"flowmeter">[] = [
        {
            sensorElementConfigKey: "flowmeter",
            parameters: { kFactor, minimumFrequency: minRawValueHz, frequencyOffset },
            legalConvertedRange: [0, null],
            subChannel: DirectSubChannel.PULSE_FREQUENCY,
        },
    ];

    return {
        communicationMode: SensorPortCommunicationMode.DIRECT,
        requiredSubChannels: [DirectSubChannel.PULSE_FREQUENCY],
        optionalSubChannels: [],
        isPermanentlyAttached: false,
        parameterConfigs: {},
        manufacturerName: "Badger Meter",
        modelName,
        friendlyName,
        friendlyShortName,
        generateSensorElements: () => sensorElements,
        migrationProperties: {
            legacySensorType,
            legacyParametersToClusterParameters: () => ({}),
            clusterParametersToLegacyParameters: () => ({}),
        },
    };
}

export const badgermeterFlowmeterClusterConfigs: Record<string, SensorClusterConfig<Record<string, never>>> = {
    // Badger Meter 228PV15xx
    badgerMeterFlowmeterOneHalfInch: badgermeterFlowmeterCluster({
        kFactor_old: 6.4238238,
        frequencyOffset: -0.316,
        minRawValueHz: 3.26,
        modelName: "228PV15",
        friendlyName: `1 1/2" Flowmeter`,
        friendlyShortName: `1 1/2" Flow`,
        legacySensorType: "badgerMeterFlowmeterOneHalfInch",
    }),

    // Badger Meter 228PV20xx
    badgerMeterFlowmeterTwoInch: badgermeterFlowmeterCluster({
        kFactor_old: 10.7615,
        frequencyOffset: 0.1435,
        minRawValueHz: 3.374,
        modelName: "228PV20",
        friendlyName: `2" Flowmeter`,
        friendlyShortName: `2" Flow`,
        legacySensorType: "badgerMeterFlowmeterTwoInch",
    }),

    // Badger Meter 228PV30xx
    badgerMeterFlowmeterThreeInch: badgermeterFlowmeterCluster({
        kFactor_old: 31.4528886,
        frequencyOffset: 0.227,
        minRawValueHz: 2.18,
        modelName: "228PV30",
        friendlyName: `3" Flowmeter`,
        friendlyShortName: `3" Flow`,
        legacySensorType: "badgerMeterFlowmeterThreeInch",
    }),

    // Badger Meter 228PV40xx
    badgerMeterFlowmeterFourInch: badgermeterFlowmeterCluster({
        kFactor_old: 52.022108682,
        frequencyOffset: 0.23707,
        minRawValueHz: 2.674,
        modelName: "228PV40",
        friendlyName: `4" Flowmeter`,
        friendlyShortName: `4" Flow`,
        legacySensorType: "badgerMeterFlowmeterFourInch",
    }),
};
